import {
    CreateReservationRoomDto,
    useCreateCartItemMutation,
    useDeleteCartItemByIdMutation,
} from 'store/api-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { useState } from 'react'
import { handleError } from 'helpers/errors'
import { useApp } from 'store/steps'
import { v4 as uuidv4 } from 'uuid'
import { useErrorMessages } from 'utils/hooks/useErrorMessages'
import {
    AccommodationEditPoint,
    BungalowInfo,
    removeAccommodationFromStore,
} from 'store/accommodation-slice'
import { useCart } from 'store/cart-slice/hooks'
import { useDispatch } from 'react-redux'
import { getDatesBetweenDates } from 'helpers/dates'
import { useCityCart } from 'components/common/CityBreak/utils/useCityCart'
import { useTickets } from 'store/ticket-slice/hooks'

interface removeAccommodationFromCartProps {
    shouldSkipRefetch?: boolean
    shouldPreserveCityBreak?: boolean
    editPoint?: AccommodationEditPoint
}

export const useAccommodation = (
    productId?: string
): {
    addAccommodationToCart: () => Promise<void>
    editAccommodationInCart: () => Promise<void>
    removeAccommodationFromCart: (
        params?: removeAccommodationFromCartProps
    ) => Promise<void>
    isAccommodationLoading: boolean
    isAccommodationDeleting: boolean
    isAccommodationProceeding: boolean
    accommodationDates: string[]
} => {
    const [isAccommodationLoading, setIsAccommodationLoading] = useState(false)
    const { setErrorMessages } = useErrorMessages()
    const dispatch = useDispatch()
    const cartAccommodation = useAppSelector(
        (state) => state.cart.items.accommodation
    )
    const { detail } = cartAccommodation || {}
    const { dateFrom, dateTo } = detail || {}
    const {
        dates,
        guestsToAssign,
        bookingPerson,
        specialRequests,
        bungalows,
        isAssignGuestsVisible,
        isAccommodationPromotionApplicable,
    } = useAppSelector((state) => state.accommodation)
    const { language } = useApp()
    const { removeAllTicketsFromCart } = useTickets()
    const {
        cartItems: cityBreakCartTickets,
        addItemsToCart: addCityBreakTicketsToCart,
        deleteItemsFromCart: removeCityBreakTicketsFromCart,
    } = useCityCart()

    const [deleteCartItem, deleteStatus] = useDeleteCartItemByIdMutation()
    const [postCartItem] = useCreateCartItemMutation()
    const { refetchCart, cartCreatorType } = useCart()

    const isAccommodationDeleting = deleteStatus.isLoading
    const isAccommodationProceeding = isAssignGuestsVisible

    const accommodationDates =
        dateFrom && dateTo ? getDatesBetweenDates(dateFrom, dateTo) : []

    const reduceRooms = (roomsToReduce: BungalowInfo[]) => {
        const cleanedRooms = roomsToReduce.map(({ room }) => {
            const { roomId, rateId, occupancyId, rateName } = room || {}
            if (roomId && rateId && occupancyId) {
                return {
                    roomId,
                    rateId,
                    occupancyId,
                    rateName,
                    roomName: rateName,
                    quantity: 1,
                }
            }
            return undefined
        })
        return cleanedRooms.reduce(
            (addedRooms: CreateReservationRoomDto[], roomToAdd) => {
                if (roomToAdd) {
                    const sameIdRoomIndex = addedRooms.findIndex(
                        (addedRoom) =>
                            addedRoom.roomId === roomToAdd.roomId &&
                            addedRoom.occupancyId === roomToAdd.occupancyId &&
                            addedRoom.rateId === roomToAdd.rateId
                    )
                    if (sameIdRoomIndex !== -1) {
                        addedRooms[sameIdRoomIndex].quantity += 1
                    } else {
                        addedRooms.push(roomToAdd)
                    }
                }
                return addedRooms
            },
            []
        )
    }

    const addAccommodationToCart = async (
        cartItemId?: string,
        quantity?: number
    ) => {
        setIsAccommodationLoading(true)
        const reducedRooms = reduceRooms(bungalows)

        const adultGuests = bungalows
            .map(({ adults, room }) =>
                adults.map(({ name, surname }) => ({
                    firstName: name,
                    lastName: surname,
                    boxId: room
                        ? `${room.occupancyId}_${room.roomId}_${room.rateId}_${room.bungalowIndex}`
                        : '',
                    personType: 'adult',
                }))
            )
            .flat()

        const kidsGuests = bungalows
            .map(({ kids, room }) =>
                kids.map(({ name, surname }) => ({
                    firstName: name,
                    lastName: surname,
                    boxId: room
                        ? `${room.occupancyId}_${room.roomId}_${room.rateId}_${room.bungalowIndex}`
                        : '',
                    personType: 'kid',
                }))
            )
            .flat()

        const infantGuests = bungalows
            .map(({ infants, room }) =>
                infants.map(({ name, surname }) => ({
                    firstName: name,
                    lastName: surname,
                    boxId: room
                        ? `${room.occupancyId}_${room.roomId}_${room.rateId}_${room.bungalowIndex}`
                        : '',
                    personType: 'infant',
                }))
            )
            .flat()

        const guests = [...adultGuests, ...kidsGuests, ...infantGuests].flat()

        if (!productId) return

        // in standard creator path - if promo preselected:
        // - remove regular tickets to park
        // - add city break tickets
        if (isAccommodationPromotionApplicable) {
            await removeAllTicketsFromCart()
            await addCityBreakTicketsToCart(true)
        }

        try {
            const postCartItemData = await postCartItem({
                createCartItemCommand: {
                    cartItemId: cartItemId || uuidv4(),
                    productId,
                    productType: 'accommodation',
                    quantity: quantity || 1,
                    detail: {
                        dateFrom: dates[0] || '',
                        dateTo: dates[dates.length - 1] || '',
                        adultCount: guestsToAssign.adults,
                        childCount: guestsToAssign.kids,
                        customerFirstName: bookingPerson.name,
                        customerLastName: bookingPerson.surname,
                        customerEmail: bookingPerson.email,
                        specialRequests: {
                            guests,
                            request: specialRequests,
                        },
                        rooms: reducedRooms,
                        customerPhone: bookingPerson.phone,
                    },
                },
                cartCreatorType,
                _locale: language,
            })

            setIsAccommodationLoading(false)
            handleError(postCartItemData)
            refetchCart(true)
        } catch (error) {
            setErrorMessages(error)
            throw error
        }
    }

    const removeAccommodationFromCart = async (
        params?: removeAccommodationFromCartProps
    ) => {
        const { shouldSkipRefetch, shouldPreserveCityBreak, editPoint } =
            params || {}

        setIsAccommodationLoading(true)

        const { cartItemId } = cartAccommodation || {}
        if (!cartItemId) {
            dispatch(removeAccommodationFromStore(editPoint))
            setIsAccommodationLoading(false)
            return
        }

        // in standard creator path - remove city break tickets along with accommodation
        if (!shouldPreserveCityBreak && cityBreakCartTickets.length > 0)
            await removeCityBreakTicketsFromCart(true)

        try {
            handleError(
                await deleteCartItem({
                    cartItemId,
                    _locale: language,
                    cartCreatorType,
                })
            )
            !shouldSkipRefetch && refetchCart()
        } catch (error) {
            setErrorMessages(error)
            throw error
        } finally {
            !shouldSkipRefetch &&
                dispatch(removeAccommodationFromStore(editPoint))
            setIsAccommodationLoading(false)
        }
    }

    const editAccommodationInCart = async () => {
        const { cartItemId, quantity } = cartAccommodation || {}
        const isAccommodationInCart = !!cartItemId

        try {
            if (isAccommodationInCart) {
                if (!cartItemId || !quantity) return
                handleError(
                    await removeAccommodationFromCart({
                        shouldSkipRefetch: true,
                        // do not remove city break tickets if potentially only edited second stap of accommo,
                        // editing on the first step clears all anyway
                        shouldPreserveCityBreak: true,
                    })
                )
                handleError(await addAccommodationToCart(cartItemId, quantity))
            } else {
                handleError(await addAccommodationToCart())
            }
        } catch (error) {
            setErrorMessages(error)
            throw error
        }
    }

    return {
        addAccommodationToCart,
        editAccommodationInCart,
        removeAccommodationFromCart,
        isAccommodationLoading,
        isAccommodationDeleting,
        isAccommodationProceeding,
        accommodationDates,
    }
}
